import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ElementRef
} from "@angular/core";
import {UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {User} from "src/app/user-admin-module/models";
import {forkJoin, of, Subject} from "rxjs";
import {Actions, ofType} from "@ngrx/effects";
import * as dealActions from "../../store/actions/deal.actions";
import {AlertService} from "src/app/shared-module/services";
import {ActivatedRoute, NavigationExtras, Router} from "@angular/router";
import {AuthService} from "src/app/auth-module/services";
import {DealService, VehicleService} from "../../services";
import {catchError, takeUntil} from "rxjs/operators";
import {NewDealRequest, Vehicle} from "../../models";
import {FORM_INPUT_REQS, USER_ROLES} from "src/app/app.config";
import {CnaService} from "../../services/cna.service";
import {otherORCounties, topORCounties} from "../../../util/counties";
import {otherStates, topStates} from "../../../util/states";
import {MatDialog} from "@angular/material/dialog";
import {
  CustomerPrioritiesDialogComponent
} from "../../../shared-module/components/dialogs/customer-priorities-dialog/customer-priorities-dialog.component";
import swal, { SweetAlertOptions } from "sweetalert2";
import { VehicleUnavailableError } from "../../models/vehicle";

@Component({
  selector: "app-new-deal",
  templateUrl: "./new-deal.component.html",
  styleUrls: ["./new-deal.component.scss"]
})
export class NewDealComponent implements OnInit, AfterViewInit, OnDestroy {
  // google places
  @Input() addressType: string = 'address';
  @Output() setAddress: EventEmitter<any> = new EventEmitter();
  @ViewChild('addressText') addressText: any;
  @ViewChild('phoneInput') phoneInput: any;
  @ViewChild('countyInput') countyInput: any;
  @ViewChild('countySelect') countySelect: any;

  private unsubscribe$ = new Subject();
  private user: User;
  uiState = {busy: false, formInitialized: false};
  validationErrors: string[] = [];

  public readonly topStates = topStates;
  public readonly otherStates = otherStates;

  public readonly topORCounties = topORCounties;
  public readonly otherORCounties = otherORCounties;

  public isSalesFloor: boolean;
  public isDealerTrade: boolean;
  public REQS = FORM_INPUT_REQS;

  newDealForm: UntypedFormGroup = this.formBuilder.group({
    customer: this.formBuilder.group({
      companyName: [""],
      firstName: ["", [Validators.required, Validators.pattern('(?![Tt][Ee][Ss][Tt]$).*')]],
      middleName: [""],
      lastName: ["", [Validators.required, Validators.pattern('(?![Tt][Ee][Ss][Tt]$).*')]],
      street: [""],
      city: [""],
      county: ["", Validators.required],
      state: ["", Validators.required],
      zip: ["", [
        Validators.minLength(5),
        Validators.maxLength(5),
        Validators.pattern("[0-9]*")
      ]],
      phone: ["", [
        Validators.minLength(10),
        Validators.maxLength(10),
        Validators.pattern("[0-9]*")
      ]],
      email: ["",],
      driverId: [""]
    }),
    vehicle: this.formBuilder.group({
      stockNumber: ["", [
        Validators.required
      ]],
      tradeCondition: [""],
      year: [""],
      modelNumber: [""],
      make: [""],
      model: [""],
      trim: [""],
      vin: [""],
      msr: [""],
      retail: [""],
      salesId: [""], // READONLY
      odometer: [null, Validators.min(0)],
      plateNumber: [""],
      plateExpires: [""],
    })
  });

  constructor(
    private formBuilder: UntypedFormBuilder,
    private actions$: Actions,
    private router: Router,
    private vehicleService: VehicleService,
    private alertService: AlertService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private dealService: DealService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog,
    public cnaService: CnaService,
  ) {
  }

  ngOnInit() {
    this.vehicleService.dispatchClearVehicle();
    this.subToActionFailure();
    this.subToUser();
    this.subToVehicle();

    this.cnaService.firstName$.pipe(takeUntil(this.unsubscribe$)).subscribe(
      firstName => {
        //console.log('firstName changed:', firstName);
        this.newDealForm.controls.customer.patchValue({firstName});
      }
    );

    this.cnaService.lastName$.pipe(takeUntil(this.unsubscribe$)).subscribe(
      lastName => {
        //console.log('lastName changed:', lastName);
        this.newDealForm.controls.customer.patchValue({lastName});
      }
    );

    this.openGetInventoryDialog();
  }

  ngAfterViewInit() {
    this.getPlaceAutocomplete();
  }

  openGetInventoryDialog() {
    this.dialog.open(CustomerPrioritiesDialogComponent, {
      width: '575px',
      autoFocus: false,
      // Test-only hook. panelClass adds an inert CSS class to the dialog container so the QA suite can locate it. No behavior or styling impact.
      panelClass: 'customer-priorities-modal',
    });
  }

  private getPlaceAutocomplete() {
    const autocomplete = new google.maps.places.Autocomplete(this.addressText.nativeElement, {
      componentRestrictions: {country: 'US'},
      types: [this.addressType]  // 'establishment' / 'address' / 'geocode'
    });
    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      const place = autocomplete.getPlace();
      this.invokeEvent(place);
    });
  }

  invokeEvent(place: any) {
    //console.log("Assigned auto complete address:", place)
    if (place?.address_components?.length > 0) {
      const partByName: Map<string, string> = new Map([]);
      place.address_components.forEach((part: any) => {
        part.types.forEach((type: string) => {
          partByName.set(
            type,
            (type === 'administrative_area_level_1' || type === 'route') ?
              part.short_name :
              part.long_name);
        });
      });

      const street = ((partByName.get('street_number') || '') + " " + (partByName.get('route') || '') + " " + (partByName.get('subpremise') || ''))
        .trim()
        .replace(/southwest/gi, "SW")
        .replace(/southeast/gi, "SE")
        .replace(/northwest/gi, "NW")
        .replace(/northeast/gi, "NE")
        .replace(/\bStreet\b/gi, "St")
        .replace(/\bAvenue\b/gi, "Ave")
        .replace(/\bBoulevard\b/gi, "Blvd")
        .replace(/\bDrive\b/gi, "Dr")
        .replace(/\bRoad\b/gi, "Rd")
        .replace(/\bLane\b/gi, "Ln")
        .replace(/\bCourt\b/gi, "Ct")
        .replace(/\bCircle\b/gi, "Cir")
        .replace(/\bPlace\b/gi, "Pl")
        .replace(/\bParkway\b/gi, "Pkwy")
        .replace(/\bHighway\b/gi, "Hwy")
        .replace(/\bTerrace\b/gi, "Ter")
        .replace(/\bTrail\b/gi, "Trl");

      const city = (partByName.get('locality') || '').trim();
      const state = (partByName.get('administrative_area_level_1') || '').trim();
      const county = (partByName.get('administrative_area_level_2') || '').replace('County', '').trim();
      const zip = (partByName.get('postal_code') || '').trim();

      this.newDealForm.controls.customer.patchValue({
        street,
        city,
        state,
        county,
        zip,
      });

      // Also update the autocomplete input field to show abbreviated address
      if (this.addressText && this.addressText.nativeElement) {
        // Use setTimeout to ensure Google has finished updating the field
        setTimeout(() => {
          let currentValue = this.addressText.nativeElement.value;
          if (currentValue) {
            // Apply the same replacements as the street field
            currentValue = currentValue
              // Direction replacements
              .replace(/southwest/gi, "SW")
              .replace(/southeast/gi, "SE")
              .replace(/northwest/gi, "NW")
              .replace(/northeast/gi, "NE")
              // Street suffix replacements
              .replace(/\bStreet\b/gi, "St")
              .replace(/\bAvenue\b/gi, "Ave")
              .replace(/\bBoulevard\b/gi, "Blvd")
              .replace(/\bDrive\b/gi, "Dr")
              .replace(/\bRoad\b/gi, "Rd")
              .replace(/\bLane\b/gi, "Ln")
              .replace(/\bCourt\b/gi, "Ct")
              .replace(/\bCircle\b/gi, "Cir")
              .replace(/\bPlace\b/gi, "Pl")
              .replace(/\bParkway\b/gi, "Pkwy")
              .replace(/\bHighway\b/gi, "Hwy")
              .replace(/\bTerrace\b/gi, "Ter")
              .replace(/\bTrail\b/gi, "Trl");
            
            this.addressText.nativeElement.value = currentValue;
          }
        }, 0);
      }

      // focus on the county select if the state is OR
      if (state === 'OR') {
        setTimeout(() => {
          const select = this.countySelect.nativeElement;
          select.click();
          select.focus();
        });
      } else {
        this.countyInput.nativeElement.focus();
      }

      // detect ui changes (required for county select)
      this.changeDetectorRef.detectChanges();
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // INITIALIZATION

  private subToActionFailure() {
    this.actions$.pipe(
      takeUntil(this.unsubscribe$),
      ofType(dealActions.dealApiFailure)
    ).subscribe(action => {
      this.alertService.error(action.error);
      this.uiState.busy = false;
    });
  }

  private subToVehicle() {
    this.vehicleService.selectVehicle()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((vehicle: Vehicle) => {
        //console.log("Step 2",vehicle)
        this.newDealForm.patchValue({
          vehicle: {
            stockNumber: vehicle.stockNumber,
            modelNumber: vehicle.modelNumber,
            year: vehicle.year,
            make: vehicle.make,
            model: vehicle.model,
            trim: vehicle.trim
          }
        });
      });
  }

  subToUser() {
    this.authService.selectUser()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user: User) => {
        if (user) {
          this.user = user;
          const userRoles = User.unpackSecurityGroups(user.securityGroups);
          if (userRoles.includes(USER_ROLES.sales_floor)) {
            this.isSalesFloor = true;
          }
          if (!this.uiState.formInitialized) {
            this.uiState.formInitialized = true;
            this.initializeForm();
          }
        }
      });
  }

  initializeForm() {
    const params = this.route.snapshot.queryParams;
    const stockNumber: string = this.route.snapshot.paramMap.get("stockNumber") || "";
    const salesId = this.user.employeeId || "";
    this.newDealForm.controls.vehicle.patchValue({salesId, stockNumber});
    this.newDealForm.controls.customer.patchValue({
      companyName: params.CompanyName ? params.CompanyName : "",
      firstName: params.FirstName ? params.FirstName : "",
      middleName: params.MiddleName ? params.MiddleName : "",
      lastName: params.LastName ? params.LastName : "",
      street: params.Address1 ? this.fullAddress(params.Address1, params.Address2) : "",
      city: params.City ? params.City : "",
      state: params.IssuedBy ? params.IssuedBy : "",
      zip: params.PostalCode ? params.PostalCode : "",
      driverId: params.LicenseNumber ? params.LicenseNumber : null
    });

    if (this.cnaService?.firstName) {
      this.newDealForm.controls.customer.patchValue({firstName: this.cnaService?.firstName});
    }
    if (this.cnaService?.lastName) {
      this.newDealForm.controls.customer.patchValue({lastName: this.cnaService?.lastName});
    }
    if (this.cnaService?.vehicle) {
      this.vehicleService.dispatchSetVehicleByLookup(this.cnaService?.vehicle);
      this.dealService.updateVehicle(this.cnaService?.vehicle, false);
    }
  }

  // ACTIONS

  onSelectModelNumberLookup() {
    this.router.navigate([{
      outlets: {
        modal: ["model-lookup"]
      },
    }]);
  }

  toggleDealerTrade() {
    this.isDealerTrade = !this.isDealerTrade;
    if (this.isDealerTrade) {
      this.setDealerTradeFormValidators();
    } else {
      this.clearDealerTradeFormValidators();
    }
    this.isDealerTrade ? this.newDealForm.get("vehicle")
        .get("stockNumber")
        .disable() :
      this.newDealForm.get("vehicle")
        .get("stockNumber")
        .enable();
  }

  setDealerTradeFormValidators() {
    this.newDealForm.get("vehicle").get("stockNumber").reset();
    this.newDealForm.get("vehicle").get("make").setValidators(Validators.required);
    this.newDealForm.get("vehicle").get("model").setValidators(Validators.required);
    this.newDealForm.get("vehicle").get("trim").setValidators(Validators.required);
    this.newDealForm.get("vehicle").get("vin").setValidators(Validators.required);
    this.newDealForm.get("vehicle").get("msr").setValidators(Validators.required);
    this.newDealForm.get("vehicle").get("retail").setValidators(Validators.required);
    this.newDealForm.get("vehicle").get("tradeCondition").setValidators(Validators.required);
    this.newDealForm.get("vehicle").get("year").setValidators(Validators.required);
  }

  clearDealerTradeFormValidators() {
    this.newDealForm.get("vehicle").get("make").clearValidators();
    this.newDealForm.get("vehicle").get("year").clearValidators();
    this.newDealForm.get("vehicle").get("model").clearValidators();
    this.newDealForm.get("vehicle").get("trim").clearValidators();
    this.newDealForm.get("vehicle").get("vin").clearValidators();
    this.newDealForm.get("vehicle").get("msr").clearValidators();
    this.newDealForm.get("vehicle").get("retail").clearValidators();
    this.newDealForm.get("vehicle").get("make").updateValueAndValidity();
    this.newDealForm.get("vehicle").get("model").updateValueAndValidity();
    this.newDealForm.get("vehicle").get("trim").updateValueAndValidity();
    this.newDealForm.get("vehicle").get("vin").updateValueAndValidity();
    this.newDealForm.get("vehicle").get("msr").updateValueAndValidity();
    this.newDealForm.get("vehicle").get("retail").updateValueAndValidity();
    this.newDealForm.get("vehicle").get("make").reset();
    this.newDealForm.get("vehicle").get("model").reset();
    this.newDealForm.get("vehicle").get("trim").reset();
    this.newDealForm.get("vehicle").get("vin").reset();
    this.newDealForm.get("vehicle").get("msr").reset();
    this.newDealForm.get("vehicle").get("retail").reset();
  }

  openVehicleLookup() {
    const navigationExtras: NavigationExtras = {
      queryParams: {changeDealOnSelectVehicle: false},
    };
    this.router.navigate([{
      outlets: {
        modal: ["vehicle-lookup"]
      },
    }], navigationExtras);
  }

  onSubmitNewDealForm() {
    // Mark all fields as touched to show validation errors
    this.newDealForm.markAllAsTouched();

    // Check if form is valid
    if (this.newDealForm.invalid) {
      this.collectValidationErrors();
      // Scroll to top to show error messages
      this.scrollToTop();
      return;
    }

    // Clear any previous validation errors
    this.validationErrors = [];

    const { stockNumber } = this.newDealForm.value.vehicle;

    // Check vehicle status before proceeding
    if (stockNumber && !this.isDealerTrade) {
      // Show loading indicator
      swal.fire({
        title: 'Checking Vehicle Availability',
        html: '<div class="swal-spinner"></div><div style="margin-top: 10px;">Please wait...</div>',
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showClass: {
          popup: '',
          backdrop: 'swal2-noanimation'
        },
        hideClass: {
          popup: ''
        }
      });

      forkJoin({
        vehicleStatus: this.vehicleService.checkVehicleStatus(stockNumber).pipe(
          catchError(error => {
            swal.close(); // Close loading indicator
          
            console.error('Error checking vehicle status:', error);
            // If status check fails, proceed anyway
            this.createDeal();
            return of(null);
          })
        ),
        vehicle: this.vehicleService.get(stockNumber).pipe(
          catchError(error => {
            swal.close(); // Close loading indicator
            console.error('Error fetching vehicle:', error);
            this.createDeal();
            return of(null);
          })
        )
      })
      .subscribe({
        next: ({ vehicleStatus, vehicle }) => {
          console.log(vehicle);
          swal.close(); // Close loading indicator

          let fallbackProducts = vehicle.insuranceProducts.filter(product => product.isFallbackTermCosts);
          if (fallbackProducts.length > 0) {
            swal.fire({
              title: 'Vehicle Missing Products',
              html: 'Vehicle is missing definitions for the following product(s): ' + fallbackProducts.map(p => p.name).join(', ') + '. These may not be reflective of the actual product terms and costs. Please contact your sales manager to reimport the vehicle.',
              icon: 'warning',
              showConfirmButton: true,
              confirmButtonText: 'OK'
            } as SweetAlertOptions)
          }
          
          if (!vehicleStatus.available || vehicleStatus.status === 'CustomerFile') {
            this.vehicleService.displayVehicleUnavailableDialog(
              VehicleUnavailableError.NotAvailableForSale,
              stockNumber
            );
            // Don't proceed with deal creation
            return;
          } else {
            // Vehicle is available, proceed with deal creation
            this.createDeal();
          }
        },
        error: (error) => {
          swal.close(); // Close loading indicator
          
          console.error('Error checking vehicle status:', error);
          // If status check fails, proceed anyway
          this.createDeal();
        }
      });
    } else {
      // No stock number or is dealer trade, proceed anyway
      this.createDeal();
    }
  }

  private createDeal() {
    const {customer} = this.newDealForm.value;
    // const salesPerson = `${this.user.firstName} ${this.user.lastName}`;
    // let managerId = this.user.managerId || "";
    // let salesManager = "";
    // if (this.authService.isSalesManager(this.user)) {
    //   salesManager = `${this.user.firstName} ${this.user.lastName}`;
    //   managerId = this.user.employeeId || "";
    // }
    const {
      salesId,
      stockNumber,
      odometer,
      plateNumber,
      plateExpires,
      year,
      modelNumber,
      make,
      model,
      trim,
      vin,
      msr,
      retail,
      tradeCondition
    } = this.newDealForm.value.vehicle;

    const deal: NewDealRequest = {
      customer,
      salesId,
      managerId: this.user.managerId || "",
      salesPerson: "",
      stockNumber: stockNumber || "",
      odometer,
      year: year.toString(),
      modelNumber,
      make,
      model,
      trim,
      vin,
      msr,
      retail,
      plateNumber,
      plateExpires,
      dealerTrade: this.isDealerTrade,
      tradeCondition
    };

    //console.log("Form: ", deal);

    this.uiState.busy = true;
    this.dealService.dispatchCreateDeal(deal);
  }

  // UI RENDERING

  get waitOnAction(): boolean {
    return this.uiState.busy;
  }

  fullAddress(address1: string, address2: string): string {
    let address = address1;
    if (address2) {
      address = address + " " + address2;
    }
    return address;
  }

  // FORM VALIDATION & HELPERS

  touchedInvalid(formGroupName: string, controlName: string): boolean {
    const formGroup = this.newDealForm.get(formGroupName);
    const control = formGroup.get(controlName);
    return control.touched && control.invalid;
  }

  trimInput(formGroupName: string, controlName: string, capFirstLetter = false) {
    const formGroup = this.newDealForm.get(formGroupName);
    let value = formGroup.value[controlName].trim();
    if (capFirstLetter) {
      value = this.capitalizeFirstChar(value);
    }
    formGroup.patchValue({[controlName]: value});
  }

  capitalizeFirstChar(str: string) {
    if (str.length === 0) {
      return str;
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  private collectValidationErrors() {
    this.validationErrors = [];

    // Check customer fields
    const customerGroup = this.newDealForm.get('customer');
    if (customerGroup.get('firstName').hasError('required') || customerGroup.get('firstName').hasError('pattern')) {
      this.validationErrors.push('First Name');
    }
    if (customerGroup.get('lastName').hasError('required') || customerGroup.get('lastName').hasError('pattern')) {
      this.validationErrors.push('Last Name');
    }
    if (customerGroup.get('state').hasError('required')) {
      this.validationErrors.push('State');
    }
    if (customerGroup.get('county').hasError('required')) {
      this.validationErrors.push('County');
    }

    // Check vehicle fields
    const vehicleGroup = this.newDealForm.get('vehicle');
    if (!this.isDealerTrade && vehicleGroup.get('stockNumber').hasError('required')) {
      this.validationErrors.push('Stock Number');
    }

    // Check vehicle fields if dealer trade
    if (this.isDealerTrade) {
      if (vehicleGroup.get('make').hasError('required')) {
        this.validationErrors.push('Vehicle Make');
      }
      if (vehicleGroup.get('model').hasError('required')) {
        this.validationErrors.push('Vehicle Model');
      }
      if (vehicleGroup.get('trim').hasError('required')) {
        this.validationErrors.push('Vehicle Trim');
      }
      if (vehicleGroup.get('vin').hasError('required')) {
        this.validationErrors.push('Vehicle VIN');
      }
      if (vehicleGroup.get('msr').hasError('required')) {
        this.validationErrors.push('Vehicle MSRP');
      }
      if (vehicleGroup.get('retail').hasError('required')) {
        this.validationErrors.push('Vehicle Retail Price');
      }
      if (vehicleGroup.get('tradeCondition').hasError('required')) {
        this.validationErrors.push('Vehicle Condition');
      }
      if (vehicleGroup.get('year').hasError('required')) {
        this.validationErrors.push('Vehicle Year');
      }
    }
  }

  private scrollToTop() {
    // Find all mat-sidenav-content elements and scroll the last one (innermost)
    const sidenavContents = document.querySelectorAll('mat-sidenav-content');
    if (sidenavContents.length > 0) {
      // The last sidenav-content is the innermost one containing the actual page content
      const contentContainer = sidenavContents[sidenavContents.length - 1];
      contentContainer.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Fallback to window scroll if sidenav-content not found
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}
